<script setup lang="ts">
/**
 * Carte « vitrine » de Donoor (cagnotte, événement, boutique, adhésion,
 * association) — accueil, rayons /recherche, suggestions… : l'image
 * occupe toute la carte et les infos sont posées dessus, en blanc sur un
 * dégradé sombre qui monte du bas (Karim 2026-10-12).
 *
 * Haut : pastille (thème / type). Bas : logo + nom de l'association, titre,
 * puis le slot (chiffres propres à chaque type : progression, date et prix,
 * produits…).
 *
 * Survol (ou focus clavier) : la carte se soulève, la photo zoome lentement
 * (voile sombre en fondu, pas de filtre CSS : repeint coûteux), et le bloc du bas remonte pour révéler le bouton d'action
 * (« Faire un don »…). Rien ne bouge si l'utilisateur a demandé à réduire les
 * animations ; les variantes `hover:` de Tailwind ne s'appliquent qu'aux
 * appareils qui survolent vraiment (pas au tap sur téléphone).
 */
import { useRechercheHelpers } from "~/composables/useRechercheHelpers";

withDefaults(
  defineProps<{
    href: string;
    titre: string;
    /** UUID Directus ou URL ; `null` → fond bleu Donoor + logo en filigrane. */
    image: string | null;
    /** Texte alternatif de l'image (le titre par défaut). */
    alt?: string;
    pastille?: string;
    /** Ligne « logo + nom » au-dessus du titre ; remplaçable par le slot `entete`. */
    associationNom?: string;
    associationLogo?: string | null;
    /** Bouton révélé au survol : « Faire un don », « Réserver »… */
    action: string;
    /**
     * Carte du PREMIER écran : image chargée tout de suite, en priorité haute
     * (`lazy` sur une image déjà visible retarde sa requête — audit lenteur
     * images 2026-09-19).
     */
    priority?: boolean;
    /**
     * Titre sur UNE ligne, terminé par « … » s'il est trop long (cartes
     * d'associations, Karim 2026-10-15). Sinon : deux lignes au plus.
     */
    titreUneLigne?: boolean;
  }>(),
  {
    alt: undefined,
    pastille: undefined,
    associationNom: undefined,
    associationLogo: null,
    priority: false,
    titreUneLigne: false,
  },
);

// Image redimensionnée (900 px webp) + repli sur l'image brute si Directus
// refuse la transformation — mêmes règles que les cartes de /recherche.
const { getImageUrl, onImageError } = useRechercheHelpers();
</script>

<template>
  <a
    :href="href"
    target="_blank"
    rel="noopener"
    class="group relative isolate block overflow-hidden aspect-[5/4] rounded-[24px] bg-[#0c3097] no-underline shadow-[0_6px_24px_rgba(12,48,151,0.10)] transition-[translate,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1.5 hover:shadow-[0_22px_48px_-12px_rgba(12,48,151,0.35)] focus-visible:-translate-y-1.5 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-(--color-main2) motion-reduce:transition-none motion-reduce:hover:translate-y-0"
  >
    <img
      v-if="image"
      :src="getImageUrl(image, undefined, 900)"
      :data-asset-id="image"
      :alt="alt ?? titre"
      class="absolute inset-0 -z-10 w-full h-full object-cover [will-change:scale] [backface-visibility:hidden] transition-[scale] duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.08] group-focus-visible:scale-[1.08] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
      :loading="priority ? 'eager' : 'lazy'"
      :fetchpriority="priority ? 'high' : 'auto'"
      @error="onImageError"
    />
    <!-- Sans image : slot `fond` (ex. logo flouté d'une association), sinon
         fond noir carbone + logo Donoor (D blanc, cœur noir) — Karim 2026-10-13. -->
    <div v-else class="absolute inset-0 -z-10 overflow-hidden">
      <slot name="fond">
        <div
          class="absolute inset-0 flex items-center justify-center pb-24 bg-[#232323]"
        >
          <img
            src="/newLogo/logo-mark-black.svg"
            width="96"
            height="96"
            alt=""
            class="w-24 h-24 object-contain transition-[scale,rotate] duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110 group-hover:-rotate-6 motion-reduce:transition-none"
          />
        </div>
      </slot>
    </div>

    <!-- Dégradé de lisibilité, renforcé au survol (le bloc du bas monte). -->
    <div
      class="absolute inset-x-0 bottom-0 h-[75%] -z-10 bg-gradient-to-t from-black/85 via-black/45 to-transparent"
      aria-hidden="true"
    />
    <div
      class="absolute inset-x-0 bottom-0 h-full -z-10 bg-gradient-to-t from-black/65 via-black/25 to-black/10 opacity-0 transition-opacity duration-700 group-hover:opacity-100 group-focus-visible:opacity-100"
      aria-hidden="true"
    />

    <div class="absolute top-3.5 inset-x-3.5 flex items-start">
      <span
        v-if="pastille"
        class="inline-flex items-center px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-xs text-white transition-colors duration-300 group-hover:bg-white/30"
        style="font-weight: 600 !important"
      >
        {{ pastille }}
      </span>
    </div>

    <div class="absolute inset-x-0 bottom-0 p-4 lg:p-5 text-white">
      <slot name="entete">
        <div v-if="associationNom" class="flex items-center gap-2 min-w-0">
          <OrgLogo
            :logo="associationLogo"
            :name="associationNom"
            size-class="w-7 h-7"
            rounded-class="rounded-full"
            ring-class="border border-white/60"
            :width="64"
          />
          <p class="text-[13px] sm:text-sm text-white/85 truncate">
            {{ associationNom }}
          </p>
        </div>
      </slot>
      <!-- Police du texte courant (h3 forcé en police d'affichage par
           main.css, d'où le style en ligne). -->
      <h3
        class="mt-2 text-base sm:text-lg text-white"
        :class="titreUneLigne ? 'truncate' : 'line-clamp-2'"
        style="font-family: var(--font-body) !important; font-weight: 600 !important; line-height: 1.3 !important"
      >
        {{ titre }}
      </h3>
      <div class="mt-3">
        <slot />
      </div>

      <!-- Bouton révélé : la rangée passe de 0 à sa hauteur (grid 0fr → 1fr),
           ce qui fait remonter tout le bloc ancré en bas. -->
      <div
        class="grid grid-rows-[0fr] transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:grid-rows-[1fr] group-focus-visible:grid-rows-[1fr] motion-reduce:transition-none"
      >
        <div class="overflow-hidden">
          <span
            class="mt-3.5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-sm text-[#0c3097] opacity-0 translate-y-2 transition-[opacity,translate] duration-500 delay-75 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:opacity-100 group-hover:translate-y-0 group-focus-visible:opacity-100 group-focus-visible:translate-y-0 motion-reduce:transition-none"
            style="font-weight: 700 !important"
          >
            {{ action }}
            <svg
              class="w-4 h-4 transition-[translate] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] -translate-x-1 group-hover:translate-x-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </span>
        </div>
      </div>
    </div>
  </a>
</template>
